// @flow

import './Navigation.scss';

import { BOILogoNoSpace, HamburguerMenu, XIcon } from '@boi/core/lib';
import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { black, greyDark } from '../../constants/colors';
import type { NavigationItem } from '../../types/commonTypes';
import { isEdgeBrowser } from '../../utils/utils';
import NavigationGroup from './NavigationGroup';

interface Props {
  menuItems: NavigationItem[][];
}

// eslint-disable-next-line complexity
const Navigation = ({ menuItems }: Props) => {
  const [isOpen, toggleOpen] = useState(false);
  const [isDisabled, setDisabled] = useState(false);
  const location = useLocation();
  const prevLocation = useRef(location.pathname);

  const className = 'c-Navigation';

  const setMenuState = (state: boolean) => {
    if (isDisabled) return;
    setDisabled(true);
    setTimeout(() => setDisabled(false), 500);
    toggleOpen(state);
  };

  const openMenu = () => setMenuState(true);

  const closeMenu = () => setMenuState(false);

  useEffect(() => {
    const handleUrlChange = () => {
      closeMenu();
    };

    if (prevLocation.current !== location.pathname) {
      handleUrlChange();
      prevLocation.current = location.pathname;
    }
  }, [location.pathname]);

  useEffect(() => {
    const container = document.getElementById(
      `${className}__burgerMenuContainer`
    );
    if (container) {
      container.addEventListener('focusout', () => {
        setTimeout(() => {
          if (!container.contains(document.activeElement)) {
            closeMenu();
          }
        }, 0);
      });
    }
  }, []);

  const getNavigationGroup = (isOpen: boolean) => (
    items: Array<NavigationItem>,
    index: number
  ) => (
    <NavigationGroup
      id={`NavigationGroup-${index}`}
      data-testid={`NavigationGroup-${index}`}
      key={`nagivation-group-${index}`}
      menuItems={items}
      menuOpen={isOpen}
      closeMenu={closeMenu}
    />
  );

  const openLabelTxt = `Toggle Menu button`;
  const closeLabelTxt = `close Menu button`;

  return (
    <div id={`${className}__burgerMenuContainer`}>
      <div
        onClick={openMenu}
        id="openMenu"
        aria-label={openLabelTxt}
        tabIndex={0}
        onKeyDown={(e: SyntheticKeyboardEvent<HTMLInputElement>) => {
          if (['Enter', 'ArrowDown'].includes(e.key)) {
            openMenu();
          }
        }}
      >
        {isEdgeBrowser && (
          <span className="documentationLinkText">{openLabelTxt}</span>
        )}
        <HamburguerMenu color={greyDark} />
      </div>
      {isOpen && (
        <div
          className={`${className} ${isOpen ? 'c-Navigation--open' : ''}`}
          id="Navigation"
        >
          <div
            className={`${className}__overlay ${
              isOpen ? 'c-Navigation__overlay--open' : ''
            }`}
          >
            <div
              className={`${className}__content ${
                isOpen ? 'c-Navigation__content--open' : ''
              }`}
            >
              <div className={`${className}__contentGroup`}>
                <div className={`${className}--with-padding`}>
                  <div className="c-MobileHeader__barIcon" id="closeMenu">
                    <HamburguerMenu color={greyDark} />
                  </div>
                  <div
                    className="c-MobileHeader__barIcon"
                    onClick={closeMenu}
                    aria-label={closeLabelTxt}
                    tabIndex={0}
                    onKeyDown={(
                      e: SyntheticKeyboardEvent<HTMLInputElement>
                    ) => {
                      if (e.key === 'Enter') {
                        closeMenu();
                      }
                    }}
                  >
                    {isEdgeBrowser && (
                      <span className="documentationLinkText">
                        {closeLabelTxt}
                      </span>
                    )}
                    <XIcon color={black} />
                  </div>
                </div>

                <div
                  className={`${className}--with-padding ${className}__contentGroup ${className}--blue`}
                >
                  Menu
                </div>

                {menuItems.map(getNavigationGroup(isOpen))}
              </div>
              <div
                className={`${className}__boiLogo ${className}--with-padding`}
              >
                <BOILogoNoSpace />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Navigation;
